/**
 * marketLineUtils.js
 *
 * Utilities for resolving the correct market line (specifier) to display
 * for a given event + marketId combination.
 *
 * Data contract:
 *   Each market variant in event.odds has:
 *     - id / marketId   : market type identifier
 *     - specifier       : the specific line value (e.g. "2.5")
 *     - outcome[]       : array of outcomes
 *       - outcome[].mainLine   : the "main" specifier for this event+market
 *       - outcome[].active     : boolean
 *       - outcome[].outcome_odd_value : string/number odd value
 */

function _isValidMainLineValue(raw) {
  return raw != null && String(raw).trim() !== '' && String(raw) !== 'null';
}

/**
 * Extracts the mainLine specifier from an array of market variants.
 *
 * Providers sometimes leave stale mainLine values on closed/inactive outcomes
 * while active outcomes carry the updated value. To guard against this we
 * prefer the mainLine read from an active outcome; we fall back to any
 * outcome only when no active ones are found.
 *
 * @param {Array} variants - market variants (same marketId, different specifiers)
 * @returns {string|null}
 */
export function extractMainLineSpecifier(variants) {
  if (!variants || variants.length === 0) return null;

  // Pass 1: prefer mainLine from an active outcome (more up-to-date)
  for (const variant of variants) {
    if (!variant.outcome) continue;
    for (const outcome of variant.outcome) {
      if (outcome.active && _isValidMainLineValue(outcome.mainLine)) {
        return String(outcome.mainLine).trim();
      }
    }
  }

  // Pass 2: fallback — read from any outcome
  for (const variant of variants) {
    if (!variant.outcome || variant.outcome.length === 0) continue;
    const raw = variant.outcome[0].mainLine;
    if (_isValidMainLineValue(raw)) return String(raw).trim();
  }

  return null;
}

/**
 * Returns true if the market variant has at least one active outcome with odd > 1.
 *
 * @param {Object} market
 * @returns {boolean}
 */
export function isMarketPlayable(market) {
  return !!(
    market &&
    market.outcome &&
    market.outcome.some(o => o.active && parseFloat(o.outcome_odd_value) > 1)
  );
}

/**
 * Resolves which market variant to display for a given event + marketId.
 *
 * Priority:
 *  1. User's explicit line selection (chosenSpecifier from customLines)
 *  2. Per-event mainLine — only if the variant is still playable.
 *     If the mainLine variant has all outcomes locked, fall through so the
 *     user sees a live line rather than a fully locked one.
 *  3. First playable variant (at least one active odd > 1)
 *  4. Middle variant (deterministic fallback, avoids index 0 bias)
 *  5. First variant (last resort)
 *
 * @param {Array}       matches         - All variants for this marketId in this event
 * @param {string|null} chosenSpecifier - User-selected specifier, or null/undefined
 * @returns {Object|null}
 */
export function resolveEventMarket(matches, chosenSpecifier) {
  if (!matches || matches.length === 0) return null;
  if (matches.length === 1) return matches[0];

  // 1. User selection — honored only if still playable.
  if (chosenSpecifier != null) {
    const found = matches.find(m => String(m.specifier) === String(chosenSpecifier));
    if (found && isMarketPlayable(found)) return found;
  }

  // 2. Per-event mainLine — trusted only if still playable.
  // Providers sometimes give inconsistent mainLine values across variants;
  // if the resolved variant is fully locked we fall through to step 3.
  const mainSpec = extractMainLineSpecifier(matches);
  if (mainSpec) {
    const mainVariant = matches.find(m => String(m.specifier) === mainSpec);
    if (mainVariant && isMarketPlayable(mainVariant)) return mainVariant;
  }

  // 3. First playable variant
  const playable = matches.filter(isMarketPlayable);
  if (playable.length > 0) return playable[0];

  // 4. Middle variant (deterministic fallback — avoids index-0 bias)
  return matches[Math.floor(matches.length / 2)];
}

/**
 * Resolves the default line for compact / detail views (where a single line
 * is pre-selected among multiple line variants for the same market).
 *
 * Priority:
 *  1. Per-event mainLine — only if the variant is still playable.
 *     If all its outcomes are locked, fall through so the user sees
 *     a live line rather than a fully locked one.
 *  2. First playable line
 *  3. Middle line (deterministic)
 *  4. First line
 *
 * @param {Array} lines - All variants for this market in this event
 * @returns {Object|null}
 */
export function resolveDefaultLine(lines) {
  if (!lines || lines.length === 0) return null;
  if (lines.length === 1) return lines[0];

  // 1. Per-event mainLine — trusted only if still playable.
  const mainSpec = extractMainLineSpecifier(lines);
  if (mainSpec) {
    const mainVariant = lines.find(l => String(l.specifier) === mainSpec);
    if (mainVariant && isMarketPlayable(mainVariant)) return mainVariant;
  }

  // 2. First playable line
  const playable = lines.filter(isMarketPlayable);
  if (playable.length > 0) return playable[0];

  // 3. Middle line (deterministic)
  return lines[Math.floor(lines.length / 2)];
}
